"use client";

import { c as _c } from "react/compiler-runtime";
import { CollectionLockup, CollectionLockupContent, CollectionLockupTitle } from "@opensea/app-kit";
import { readFragment } from "@opensea/graphql";
import { HandoffButton } from "@opensea/navigation";
import { classNames, FlexColumn, SpaceBetween, TextBody, Tooltip } from "@opensea/ui-kit";
import { useSelectItemMouseShortcuts } from "@opensea/ui-kit/hooks";
import { isAddressEqual } from "@opensea/utils";
import { useCreateOfferFlow } from "@opensea/wallet";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { useContextSelector } from "use-context-selector";
import { useShallow } from "zustand/react/shallow";
import { CollectionPreviewTooltip } from "@/components/collection/CollectionPreviewTooltip/CollectionPreviewTooltip";
import { TokenPrice } from "@/components/price/TokenPrice";
import { useIsOwnProfileAddress } from "@/components/profile/ProfileAddressProvider";
import { ProfileSettingsContext } from "@/components/profile/ProfileSettingsProvider";
import { EnforcementBadge } from "@/components/trustandsafety/EnforcementBadge";
import { CRYPTO_PUNKS_ADDRESS } from "@/constants/constants";
import { useStatusQueryParam } from "@/hooks/query-param/use-status-query-param";
import { useRowHighlightClassNames } from "@/hooks/use-row-highlight";
import { getItemBestOffer } from "@/lib/fragments/best-order";
import { itemCardActionVariants } from "../../card/constants";
import { useItemCardTokenPriceMarketplaceProps } from "../../card/hooks";
import { ItemCard, ItemCardActionButton, ItemCardActionSection, ItemCardActions, ItemCardChainBadge, ItemCardContent, ItemCardFooter, ItemCardFooterDescription, ItemCardFooterSection, ItemCardHiddenItemBadge, ItemCardMedia, ItemCardName, ItemCardPriceLabel, ItemCardRarityBadge, ItemCardSelectItemBadge, ItemCardSelectItemWithQuantityBadge, ItemCardTooltipButtonWrapper } from "../../card/ItemCard";
import { CollectionLink } from "../../collection/CollectionLink";
import { GridItemSkeleton } from "../../GridItemSkeleton";
import { ItemLink } from "../../ItemLink";
import { useItemView } from "../../ItemViewProvider";
import { OwnedQuantityBadge } from "../../OwnedQuantityBadge";
import { useBuyItems } from "../../use-buy-items";
import { useListItems } from "../../use-list-items";
import { useMakeOffer } from "../../use-make-offer";
import { getItemOwnedQuantity } from "../../utils";
import { isItemTradable } from "../../validation/tradable";
import { useProfileItemsSelectionStore } from "../ProfileItemsSelectionProvider";
import { useProfileItemSelectionIndex } from "../use-profile-item-selection-index";
import { ProfileItemsCardFragment } from "./ProfileItemsCard.fragment";
export function ProfileItemsCard(t0) {
  const $ = _c(80);
  const {
    item: itemKey,
    index,
    disableSelectionState
  } = t0;
  let t1;
  if ($[0] !== itemKey) {
    t1 = readFragment(ProfileItemsCardFragment, itemKey);
    $[0] = itemKey;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const item = t1;
  const [frameTime, setFrameTime] = useState(1);
  const {
    itemView
  } = useItemView();
  const [action, selectedItemsMap, selectItem, allItems, updateSelectionQuantity, isItemSelectable] = useProfileItemsSelectionStore(useShallow(_temp));
  const isGalleryAction = action === "gallery";
  const selectionIndex = useProfileItemSelectionIndex(item?.id);
  let t2;
  if ($[2] !== allItems || $[3] !== selectItem || $[4] !== selectedItemsMap) {
    t2 = {
      items: allItems,
      selectedItems: selectedItemsMap,
      selectItem
    };
    $[2] = allItems;
    $[3] = selectItem;
    $[4] = selectedItemsMap;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    handleOnClick
  } = useSelectItemMouseShortcuts(t2);
  const {
    showChainIcon,
    showRarity
  } = useContextSelector(ProfileSettingsContext, _temp2);
  const isOwnProfileAddress = useIsOwnProfileAddress();
  const highlightClassNames = useRowHighlightClassNames(item?.id);
  const {
    statuses
  } = useStatusQueryParam();
  let t3;
  if ($[6] !== statuses) {
    t3 = statuses.includes("hidden");
    $[6] = statuses;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const isHidden = t3;
  const priceLabel = usePriceLabel(item);
  if (!item) {
    let t4;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <GridItemSkeleton />;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    return t4;
  }
  let t4;
  if ($[9] !== disableSelectionState || $[10] !== item.id || $[11] !== selectedItemsMap) {
    t4 = !disableSelectionState && selectedItemsMap.has(item.id);
    $[9] = disableSelectionState;
    $[10] = item.id;
    $[11] = selectedItemsMap;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const active = t4;
  let t5;
  if ($[13] !== item.id || $[14] !== selectedItemsMap) {
    t5 = selectedItemsMap.get(item.id)?.quantity;
    $[13] = item.id;
    $[14] = selectedItemsMap;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const selectedQuantity = t5;
  let t6;
  if ($[16] !== disableSelectionState || $[17] !== isItemSelectable || $[18] !== item.id) {
    t6 = !disableSelectionState && isItemSelectable(item.id);
    $[16] = disableSelectionState;
    $[17] = isItemSelectable;
    $[18] = item.id;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const isSelectable = t6;
  const inSelectionMode = selectedItemsMap.size > 0;
  let t7;
  if ($[20] !== handleOnClick || $[21] !== inSelectionMode || $[22] !== index || $[23] !== isItemSelectable || $[24] !== item.id) {
    t7 = e => {
      if (inSelectionMode) {
        e.preventDefault();
        if (isItemSelectable(item.id)) {
          handleOnClick(e, index);
        }
      }
    };
    $[20] = handleOnClick;
    $[21] = inSelectionMode;
    $[22] = index;
    $[23] = isItemSelectable;
    $[24] = item.id;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  let t9;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => setFrameTime(undefined);
    t9 = () => setFrameTime(1);
    $[26] = t8;
    $[27] = t9;
  } else {
    t8 = $[26];
    t9 = $[27];
  }
  const t10 = itemView === "mosaic";
  const t11 = itemView !== "mosaic" && "aspect-square";
  let t12;
  if ($[28] !== t11) {
    t12 = classNames(t11);
    $[28] = t11;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  const t13 = itemView !== "mosaic" && "aspect-square object-contain";
  let t14;
  if ($[30] !== t13) {
    t14 = classNames("h-full", t13);
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== frameTime || $[33] !== item || $[34] !== t10 || $[35] !== t12 || $[36] !== t14) {
    t15 = <ItemCardMedia adjustAspectRatio={t10} className={t12} frameTime={frameTime} item={item} mediaClassName={t14} />;
    $[32] = frameTime;
    $[33] = item;
    $[34] = t10;
    $[35] = t12;
    $[36] = t14;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== item.chain || $[39] !== showChainIcon) {
    t16 = showChainIcon ? <ItemCardChainBadge identifier={item.chain.identifier} /> : null;
    $[38] = item.chain;
    $[39] = showChainIcon;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== active || $[42] !== handleOnClick || $[43] !== index || $[44] !== isGalleryAction || $[45] !== isOwnProfileAddress || $[46] !== isSelectable || $[47] !== item || $[48] !== selectItem || $[49] !== selectedQuantity || $[50] !== selectionIndex || $[51] !== updateSelectionQuantity) {
    t17 = isOwnProfileAddress && (isSelectable || active) ? item.isFungible && !isGalleryAction ? <ItemCardSelectItemWithQuantityBadge max={getItemOwnedQuantity(item).toNumber()} selectedQuantity={selectedQuantity ?? 0} updateQuantity={quantity => {
      if (quantity === 1 && !active) {
        selectItem(item.id);
      } else {
        updateSelectionQuantity(item.id, quantity);
      }
    }} /> : <ItemCardSelectItemBadge active={active} onClick={event => {
      handleOnClick(event, index);
    }} selectedIndex={isGalleryAction ? selectionIndex + 1 : undefined} /> : null;
    $[41] = active;
    $[42] = handleOnClick;
    $[43] = index;
    $[44] = isGalleryAction;
    $[45] = isOwnProfileAddress;
    $[46] = isSelectable;
    $[47] = item;
    $[48] = selectItem;
    $[49] = selectedQuantity;
    $[50] = selectionIndex;
    $[51] = updateSelectionQuantity;
    $[52] = t17;
  } else {
    t17 = $[52];
  }
  let t18;
  if ($[53] !== isHidden) {
    t18 = isHidden ? <ItemCardHiddenItemBadge /> : null;
    $[53] = isHidden;
    $[54] = t18;
  } else {
    t18 = $[54];
  }
  let t19;
  if ($[55] !== t15 || $[56] !== t16 || $[57] !== t17 || $[58] !== t18) {
    t19 = <ItemCardContent>{t15}{t16}{t17}{t18}</ItemCardContent>;
    $[55] = t15;
    $[56] = t16;
    $[57] = t17;
    $[58] = t18;
    $[59] = t19;
  } else {
    t19 = $[59];
  }
  let t20;
  if ($[60] !== item || $[61] !== itemView || $[62] !== priceLabel || $[63] !== showRarity) {
    t20 = itemView !== "mosaic" && <ItemCardFooter className="min-h-24"><ItemCardFooterSection className={classNames("h-[calc(theme(spacing.12)+theme(spacing.3))] pt-3", "group-hover:pt-0", "transition-[padding] duration-200 ease-out-quint")}><FlexColumn className="w-full"><SpaceBetween className="w-full items-center gap-2"><ItemLink className="min-w-0 flex-1" item={item} onClick={_temp3}><HandoffButton><ItemCardName item={item} /></HandoffButton></ItemLink>{item.isFungible ? <OwnedQuantityBadge item={item} /> : showRarity ? <ItemCardRarityBadge item={item} /> : null}</SpaceBetween>{item.collection !== null ? <CollectionPreviewTooltip collection={item.collection}><CollectionLink collection={item.collection} onClick={_temp4}><CollectionLockup className="text-secondary" collection={item.collection} size="xs"><CollectionLockupContent><CollectionLockupTitle weight="regular" /></CollectionLockupContent></CollectionLockup></CollectionLink></CollectionPreviewTooltip> : null}</FlexColumn></ItemCardFooterSection><ItemCardFooterSection className="items-center"><ItemCardFooterDescription>{item.enforcement.isCompromised ? <SpaceBetween className="mb-6" /> : <EnforcementBadge entity={item} renderWhenNotEnforced={<ItemCardPriceLabel {...priceLabel} size={itemView === "gridCompact" ? "xs" : undefined} />} />}</ItemCardFooterDescription></ItemCardFooterSection></ItemCardFooter>;
    $[60] = item;
    $[61] = itemView;
    $[62] = priceLabel;
    $[63] = showRarity;
    $[64] = t20;
  } else {
    t20 = $[64];
  }
  let t21;
  if ($[65] !== item || $[66] !== t19 || $[67] !== t20 || $[68] !== t7) {
    t21 = <ItemLink className="flex h-full flex-col" item={item} onClick={t7} onPointerEnter={t8} onPointerLeave={t9}>{t19}{t20}</ItemLink>;
    $[65] = item;
    $[66] = t19;
    $[67] = t20;
    $[68] = t7;
    $[69] = t21;
  } else {
    t21 = $[69];
  }
  let t22;
  if ($[70] !== isGalleryAction || $[71] !== item || $[72] !== itemView || $[73] !== selectedQuantity) {
    t22 = isGalleryAction ? null : <ItemCardActions floating={itemView === "mosaic"}><ProfileItemsCardActions item={item} quantity={selectedQuantity} /></ItemCardActions>;
    $[70] = isGalleryAction;
    $[71] = item;
    $[72] = itemView;
    $[73] = selectedQuantity;
    $[74] = t22;
  } else {
    t22 = $[74];
  }
  let t23;
  if ($[75] !== active || $[76] !== highlightClassNames || $[77] !== t21 || $[78] !== t22) {
    t23 = <HandoffButton><ItemCard active={active} className={highlightClassNames}>{t21}{t22}</ItemCard></HandoffButton>;
    $[75] = active;
    $[76] = highlightClassNames;
    $[77] = t21;
    $[78] = t22;
    $[79] = t23;
  } else {
    t23 = $[79];
  }
  return t23;
}
function _temp4(e_1) {
  e_1.stopPropagation();
}
function _temp3(e_0) {
  e_0.stopPropagation();
}
function _temp2(state) {
  return {
    showChainIcon: state.showChainIcon,
    showRarity: state.showRarity
  };
}
function _temp(v) {
  return [v.action, v.selectedItemsMap, v.selectItem, v.allItems, v.updateSelectionQuantity, v.isItemSelectable];
}
function usePriceLabel(item) {
  const $ = _c(21);
  const t = useTranslations("ProfileItemsCard");
  if (!item) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = {
        label: ""
      };
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  if (item.lowestListingForOwner) {
    let t0;
    if ($[1] !== t) {
      t0 = t("listed");
      $[1] = t;
      $[2] = t0;
    } else {
      t0 = $[2];
    }
    let t1;
    if ($[3] !== item.lowestListingForOwner.pricePerItem) {
      t1 = <TokenPrice price={item.lowestListingForOwner.pricePerItem} symbolColor="text-primary" />;
      $[3] = item.lowestListingForOwner.pricePerItem;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    let t2;
    if ($[5] !== t0 || $[6] !== t1) {
      t2 = {
        label: t0,
        component: t1
      };
      $[5] = t0;
      $[6] = t1;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    return t2;
  }
  let t0;
  if ($[8] !== item) {
    t0 = getItemBestOffer(item);
    $[8] = item;
    $[9] = t0;
  } else {
    t0 = $[9];
  }
  const bestOffer = t0;
  if (bestOffer) {
    let t1;
    if ($[10] !== t) {
      t1 = t("topOffer");
      $[10] = t;
      $[11] = t1;
    } else {
      t1 = $[11];
    }
    let t2;
    if ($[12] !== bestOffer.pricePerItem) {
      t2 = <TokenPrice price={bestOffer.pricePerItem} symbolColor="text-primary" />;
      $[12] = bestOffer.pricePerItem;
      $[13] = t2;
    } else {
      t2 = $[13];
    }
    let t3;
    if ($[14] !== t1 || $[15] !== t2) {
      t3 = {
        label: t1,
        component: t2
      };
      $[14] = t1;
      $[15] = t2;
      $[16] = t3;
    } else {
      t3 = $[16];
    }
    return t3;
  }
  let t1;
  if ($[17] !== t) {
    t1 = t("notListed");
    $[17] = t;
    $[18] = t1;
  } else {
    t1 = $[18];
  }
  let t2;
  if ($[19] !== t1) {
    t2 = {
      label: t1
    };
    $[19] = t1;
    $[20] = t2;
  } else {
    t2 = $[20];
  }
  return t2;
}
function ProfileItemsCardActions(t0) {
  const $ = _c(69);
  const {
    item,
    quantity: t1
  } = t0;
  const quantity = t1 === undefined ? 1 : t1;
  const t = useTranslations("ProfileItemsCard");
  const isOwnProfileAddress = useIsOwnProfileAddress();
  const {
    itemView
  } = useItemView();
  const createOffer = useCreateOfferFlow();
  let t2;
  if ($[0] !== createOffer || $[1] !== item.chain.identifier || $[2] !== item.contractAddress || $[3] !== item.tokenId || $[4] !== quantity) {
    t2 = () => {
      createOffer([{
        chain: {
          identifier: item.chain.identifier
        },
        contractAddress: item.contractAddress,
        tokenId: item.tokenId,
        quantity
      }]);
    };
    $[0] = createOffer;
    $[1] = item.chain.identifier;
    $[2] = item.contractAddress;
    $[3] = item.tokenId;
    $[4] = quantity;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handleCreateOffer = t2;
  let t3;
  if ($[6] !== item || $[7] !== quantity) {
    t3 = {
      ...item,
      quantity
    };
    $[6] = item;
    $[7] = quantity;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const itemWithQuantity = t3;
  let t4;
  if ($[9] !== itemWithQuantity) {
    t4 = [itemWithQuantity];
    $[9] = itemWithQuantity;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const {
    disabled: isBuyDisabled,
    disabledReason: buyDisabledReason,
    buyItems
  } = useBuyItems(t4);
  let t5;
  if ($[11] !== itemWithQuantity) {
    t5 = [itemWithQuantity];
    $[11] = itemWithQuantity;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const {
    disabled: isMakeOfferDisabled,
    disabledReason: makeOfferDisabledReason
  } = useMakeOffer(t5);
  let t6;
  if ($[13] !== itemWithQuantity) {
    t6 = [itemWithQuantity];
    $[13] = itemWithQuantity;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const {
    disabled: isListDisabled,
    disabledReason: listDisabledReason,
    listItems
  } = useListItems(t6);
  const isListed = Boolean(item.lowestListingForOwner);
  const tokenPriceMarketplaceProps = useItemCardTokenPriceMarketplaceProps(item.lowestListingForOwner?.marketplace?.identifier);
  if (isOwnProfileAddress) {
    const t7 = !isListDisabled;
    let t8;
    if ($[15] !== item.isFungible || $[16] !== item.lowestListingForOwner || $[17] !== t) {
      t8 = item.lowestListingForOwner && !item.isFungible ? t("editListing") : t("listItem");
      $[15] = item.isFungible;
      $[16] = item.lowestListingForOwner;
      $[17] = t;
      $[18] = t8;
    } else {
      t8 = $[18];
    }
    let t9;
    if ($[19] !== t8) {
      t9 = <ItemCardActionSection>{t8}</ItemCardActionSection>;
      $[19] = t8;
      $[20] = t9;
    } else {
      t9 = $[20];
    }
    let t10;
    if ($[21] !== isListDisabled || $[22] !== listItems || $[23] !== t9) {
      t10 = <ItemCardTooltipButtonWrapper><ItemCardActionButton disabled={isListDisabled} onClick={listItems}>{t9}</ItemCardActionButton></ItemCardTooltipButtonWrapper>;
      $[21] = isListDisabled;
      $[22] = listItems;
      $[23] = t9;
      $[24] = t10;
    } else {
      t10 = $[24];
    }
    let t11;
    if ($[25] !== listDisabledReason || $[26] !== t10 || $[27] !== t7) {
      t11 = <Tooltip content={listDisabledReason} disabled={t7}>{t10}</Tooltip>;
      $[25] = listDisabledReason;
      $[26] = t10;
      $[27] = t7;
      $[28] = t11;
    } else {
      t11 = $[28];
    }
    return t11;
  }
  if (isListed) {
    const t7 = !isBuyDisabled;
    let t8;
    if ($[29] !== buyItems) {
      t8 = () => {
        buyItems();
      };
      $[29] = buyItems;
      $[30] = t8;
    } else {
      t8 = $[30];
    }
    let t9;
    if ($[31] !== itemView) {
      t9 = itemCardActionVariants({
        itemView
      }).section();
      $[31] = itemView;
      $[32] = t9;
    } else {
      t9 = $[32];
    }
    let t10;
    if ($[33] !== t) {
      t10 = t("buyNow");
      $[33] = t;
      $[34] = t10;
    } else {
      t10 = $[34];
    }
    let t11;
    if ($[35] !== t10) {
      t11 = <TextBody>{t10}</TextBody>;
      $[35] = t10;
      $[36] = t11;
    } else {
      t11 = $[36];
    }
    let t12;
    if ($[37] !== itemView) {
      t12 = itemCardActionVariants({
        itemView
      }).tokenPrice();
      $[37] = itemView;
      $[38] = t12;
    } else {
      t12 = $[38];
    }
    const t13 = item.lowestListingForOwner?.pricePerItem;
    let t14;
    if ($[39] !== t12 || $[40] !== t13 || $[41] !== tokenPriceMarketplaceProps) {
      t14 = <TokenPrice className={t12} price={t13} symbolColor="white" valueColor="white" {...tokenPriceMarketplaceProps} />;
      $[39] = t12;
      $[40] = t13;
      $[41] = tokenPriceMarketplaceProps;
      $[42] = t14;
    } else {
      t14 = $[42];
    }
    let t15;
    if ($[43] !== t11 || $[44] !== t14 || $[45] !== t9) {
      t15 = <ItemCardActionSection className={t9}>{t11}{t14}</ItemCardActionSection>;
      $[43] = t11;
      $[44] = t14;
      $[45] = t9;
      $[46] = t15;
    } else {
      t15 = $[46];
    }
    let t16;
    if ($[47] !== isBuyDisabled || $[48] !== t15 || $[49] !== t8) {
      t16 = <ItemCardTooltipButtonWrapper><ItemCardActionButton disabled={isBuyDisabled} onClick={t8}>{t15}</ItemCardActionButton></ItemCardTooltipButtonWrapper>;
      $[47] = isBuyDisabled;
      $[48] = t15;
      $[49] = t8;
      $[50] = t16;
    } else {
      t16 = $[50];
    }
    let t17;
    if ($[51] !== buyDisabledReason || $[52] !== t16 || $[53] !== t7) {
      t17 = <Tooltip content={buyDisabledReason} disabled={t7}>{t16}</Tooltip>;
      $[51] = buyDisabledReason;
      $[52] = t16;
      $[53] = t7;
      $[54] = t17;
    } else {
      t17 = $[54];
    }
    return t17;
  }
  if (!isItemTradable(item) || isAddressEqual(item.contractAddress, CRYPTO_PUNKS_ADDRESS)) {
    return null;
  }
  const t7 = !isMakeOfferDisabled;
  let t8;
  if ($[55] !== handleCreateOffer) {
    t8 = () => {
      handleCreateOffer();
    };
    $[55] = handleCreateOffer;
    $[56] = t8;
  } else {
    t8 = $[56];
  }
  let t9;
  if ($[57] !== t) {
    t9 = t("makeOffer");
    $[57] = t;
    $[58] = t9;
  } else {
    t9 = $[58];
  }
  let t10;
  if ($[59] !== t9) {
    t10 = <ItemCardActionSection>{t9}</ItemCardActionSection>;
    $[59] = t9;
    $[60] = t10;
  } else {
    t10 = $[60];
  }
  let t11;
  if ($[61] !== isMakeOfferDisabled || $[62] !== t10 || $[63] !== t8) {
    t11 = <ItemCardTooltipButtonWrapper><ItemCardActionButton disabled={isMakeOfferDisabled} onClick={t8}>{t10}</ItemCardActionButton></ItemCardTooltipButtonWrapper>;
    $[61] = isMakeOfferDisabled;
    $[62] = t10;
    $[63] = t8;
    $[64] = t11;
  } else {
    t11 = $[64];
  }
  let t12;
  if ($[65] !== makeOfferDisabledReason || $[66] !== t11 || $[67] !== t7) {
    t12 = <Tooltip content={makeOfferDisabledReason} disabled={t7}>{t11}</Tooltip>;
    $[65] = makeOfferDisabledReason;
    $[66] = t11;
    $[67] = t7;
    $[68] = t12;
  } else {
    t12 = $[68];
  }
  return t12;
}